/**
 * TABLE ROW COMPONENT
 * Row wrapper with cell management
 * @version 1.0.0
 * @production-ready
 */

import React from 'react';
import { ColumnDefinition, TableStyleConfig } from '../types';
import { TableCell } from './TableCell';

interface TableRowProps {
  rowKey: string;
  rowIndex: number;
  columns: ColumnDefinition[];
  columnWidths: number[];
  rowData: any;
  styleConfig: TableStyleConfig;
  focusedCell: { rowIndex: number; colIndex: number };
  onCellChange: (rowKey: string, columnKey: string, value: any) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, row: number, col: number) => void;
  onFocus: (row: number, col: number) => void;
  setInputRef: (el: HTMLInputElement | null, row: number, col: number) => void;
  onRowClick?: (rowKey: string, rowIndex: number) => void;
  onRemoveRowKey?: (index: number) => void;
}

const TableRowComponent: React.FC<TableRowProps> = ({
  rowKey,
  rowIndex,
  columns,
  columnWidths,
  rowData,
  styleConfig,
  focusedCell,
  onCellChange,
  onKeyDown,
  onFocus,
  setInputRef,
  onRowClick,
  onRemoveRowKey
}) => {
  const condensed = styleConfig.condensed ?? true;
  const rowHeight = styleConfig.rowHeight ?? (condensed ? 28 : 38);
  const hoverEffect = styleConfig.hoverEffect ?? true;

  // Columns passed here are already filtered (visibleColumns), so columnWidths should match
  return (
    <tr
      className={hoverEffect ? 'hover:bg-[#F2F8FC]' : ''}
      style={{ height: `${rowHeight}px` }}
      onClick={onRowClick ? () => onRowClick(rowKey, rowIndex) : undefined}
    >
      {columns.map((column, colIndex) => {
        const isSticky = column.sticky ?? false;
        const stickyLeft = (column as any).stickyLeft ?? 0;
        const width = columnWidths[colIndex] || 120;
        
        const value = rowData[column.key];
        const isSelected = focusedCell.rowIndex === rowIndex && focusedCell.colIndex === colIndex;
        const isEditable = column.editable !== false && !column.calculated && !column.disabled;

        return (
          <td
            key={`${rowKey}-${column.key}`}
            className={column.type === 'time' ? 'time-input-cell' : ''}
            data-sticky={isSticky ? 'true' : undefined}
            data-sticky-left={isSticky ? stickyLeft : undefined}
            style={{
              width: `${width}px`,
              minWidth: `${width}px`,
              maxWidth: `${width}px`,
              height: `${rowHeight}px`,
              position: isSticky ? 'sticky' : 'relative',
              left: isSticky ? `${stickyLeft}px` : 'auto',
              zIndex: isSticky ? 10 : 1,
              background: isSticky 
                ? (rowIndex % 2 === 0 ? 'var(--color-white)' : 'var(--color-gray-50)')
                : undefined,
              borderRight: isSticky ? '2px solid var(--color-gray-400)' : undefined,
              boxShadow: isSticky ? '2px 0 4px rgba(0, 0, 0, 0.1)' : undefined,
              padding: 0,
              // Remove left border for time input cells
              borderLeft: column.type === 'time' ? 'none' : undefined,
              // Ensure sticky cells have proper background to cover scrolling content
              ...(isSticky && {
                backgroundColor: rowIndex % 2 === 0 ? 'var(--color-white)' : 'var(--color-gray-50)'
              })
            }}
          >
            <TableCell
              column={column}
              value={value}
              rowData={rowData}
              width={width}
              styleConfig={styleConfig}
              isSelected={isSelected}
              isEditable={isEditable}
              onChange={(newValue) => onCellChange(rowKey, column.key, newValue)}
              onKeyDown={(e) => onKeyDown(e, rowIndex, colIndex)}
              onFocus={() => onFocus(rowIndex, colIndex)}
              inputRef={(el) => setInputRef(el, rowIndex, colIndex)}
              onRemoveRowKey={onRemoveRowKey ? () => onRemoveRowKey(rowIndex) : undefined}
            />
          </td>
        );
      })}
    </tr>
  );
};

/**
 * Skip re-rendering a row unless something that actually affects ITS OWN output changed.
 *
 * `focusedCell` is a fresh object on every keystroke/navigation (parent state), so we
 * compare its effect on THIS row (was/is selected, and which column) rather than its
 * reference — otherwise every row would re-render on every arrow-key press.
 *
 * `rowData`/`columns`/`columnWidths`/`styleConfig` are compared by reference: the data
 * hook only replaces the edited row's object, and the column-width hook is keyed off
 * `rowKeys` (not `data`), so unrelated rows keep stable references across edits.
 *
 * Callback props (`onCellChange`, `onKeyDown`, etc.) are deliberately NOT compared:
 * several forms still recreate these closures every render (not yet `useCallback`-
 * wrapped everywhere). Skipping the comparison lets memoization engage regardless; it's
 * safe because a bailed-out row keeps whatever closure was wired up the last time this
 * exact row (same `rowKey`/`rowIndex`, i.e. same React `key`) actually rendered, and
 * that closure still reads current values through the parent's own state setters/hook
 * functions (e.g. `updateCell`) rather than stale snapshots — the same trade-off made
 * deliberately in TableCell.tsx's comparator.
 *
 * Note: for tabs whose `columns` prop is recomputed per-render (e.g. BDDetails' focus-
 * dependent dropdown options), this array reference changes every render, so this
 * comparator won't fully skip TableRow's own (cheap) render there — but each `column`
 * object inside it that didn't change stays reference-equal, so TableCell.tsx's own
 * memoization still skips the expensive per-cell work for unaffected cells.
 */
const arePropsEqual = (prev: TableRowProps, next: TableRowProps): boolean => {
  if (prev.rowKey !== next.rowKey) return false;
  if (prev.rowIndex !== next.rowIndex) return false;
  if (prev.rowData !== next.rowData) return false;
  if (prev.columns !== next.columns) return false;
  if (prev.columnWidths !== next.columnWidths) return false;
  if (prev.styleConfig !== next.styleConfig) return false;

  const wasSelected = prev.focusedCell.rowIndex === prev.rowIndex;
  const isSelectedNow = next.focusedCell.rowIndex === next.rowIndex;
  if (wasSelected !== isSelectedNow) return false;
  if (isSelectedNow && prev.focusedCell.colIndex !== next.focusedCell.colIndex) return false;

  return true;
};

export const TableRow = React.memo(TableRowComponent, arePropsEqual);
